import { BANK_ACCOUNT_QUERY_KEY } from '@/apis/bank-accounts/hooks/useGetBankAccount'
import { usePostBankAccount } from '@/apis/bank-accounts/hooks/usePostBankAccount'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Button, buttonVariants } from '@/app/common/components/Button'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { parseRequestError } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import { Input } from '@/common/components/Input.tsx'
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { setCurrentAccount, setReimbursementAccountNickname, setReimbursementAccountSubmitted } from '@/storage/applications/reimubrusementAccountSlice'
import { RootState } from '@/storage/store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { BankAccounVerificationStatusId } from '../constants'

const detailSchema = z
  .object({
    nick_name: z.string().min(1, 'nick_name_is_required'),
    terms_accepted: z.literal(true, {
      errorMap: () => ({ message: 'you_must_accept_the_terms_and_conditions' }),
    }),
  })

type FormValues = z.infer<typeof detailSchema>

export const ReimbursementAccountNickName = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { fireAlert } = useAppAlert()
  const queryClient = useQueryClient()

  const { currentAccount } = useSelector((state: RootState) => state.ReimbursementAccount)
  const programSettings = useSelector((state: RootState) => state.ProgramSettings)

  const mutation = usePostBankAccount({
    onError: (e) => {
      const error = Error(parseRequestError(e).message)
      fireAlert({
        title: true,
        titleText: error?.message ?? "",
        variant: 'error',
      })
    },
    onSuccess: () => {
        queryClient.invalidateQueries({
            queryKey: [BANK_ACCOUNT_QUERY_KEY],
        })
      navigate(AppRoutePaths.reimbursements.accounts.link.complete)
      dispatch(setReimbursementAccountSubmitted(true))
    },
  })

  useEffect(() => {
    if(currentAccount?.submitted){
      dispatch(setCurrentAccount(null))
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: {
      nick_name: currentAccount?.nickName ?? "",
    }
  })

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    dispatch(setReimbursementAccountNickname(data.nick_name))
    mutation.mutate({
      address: currentAccount?.address?.address ?? "",
      address_2: currentAccount?.address?.address2 ?? "",
      city: currentAccount?.address?.city ?? "",
      zip_code: currentAccount?.address?.zipcode ?? "",
      global_state_id: currentAccount?.address?.state ?? 0,
      account_number: currentAccount?.accountNumber ?? "",
      routing_number: currentAccount?.routingNumber ?? "",
      bank_name: currentAccount?.bankName ?? "",
      account_holder_name: currentAccount?.accountDetail?.holders_name ?? "",
      account_holder_dob: currentAccount?.accountDetail?.birth_date ?? "",
      account_type_id: currentAccount?.accountDetail?.account_type ?? 1,
      account_nickname: data.nick_name ?? "",
      verification_status_id: BankAccounVerificationStatusId.InReview
    })
  }

  const onBack = () => {
    navigate(-1)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('choose_nickname_for_this_account')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-80">
        <form
          id="form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="nick_name_label"
            >
              <Label
                id="nick_name_label"
                htmlFor="nick_name"
              >
                {translate('nickname')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="nick_name"
                  {...register('nick_name')}
                />
              </div>
              {errors.nick_name && (
                <FormErrorMessage
                  message={translate(errors.nick_name?.message ?? '')}
                />
              )}
            </section>

          </div>
          <div>
            <div className="flex items-center gap-x-3">
              <Input
                {...register('terms_accepted')}
                id="terms_accepted"
                type="checkbox"
                className="h-5 w-5 text-primary-600 focus:ring-primary-600"
              />
              <label
                htmlFor="terms_accepted"
                className="block text-sm text-md leading-6 text-gray-900"
              >
                {' '}
                {translate('registration_page.terms_and_conditions_part_one', { program_name: programSettings?.data?.name })}
                <Link
                  target="_blank"
                  to={""}
                  className={buttonVariants({
                    variant: 'text',
                    size: 'text',
                  })}
                >
                  {translate('terms_and_conditions')}
                </Link>
                .
              </label>
            </div>
            <FormErrorMessage
              message={translate(errors.terms_accepted?.message ?? '')}
            />
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="justify-center">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="px-10"
          >
            {translate('back')}
          </Button>
          {' '}          <SubmitButton
            form="form"
            status={mutation.status}
            buttonTxt={translate('next')}
            className="px-10"
          />
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
